import React, { useEffect, useMemo, useState } from "react";
import type { Participant, RemoteParticipant } from "livekit-client";
import { Track, RoomEvent, DataPacket_Kind } from "livekit-client";
import {
	useTracks,
	useIsMuted,
	useIsSpeaking,
	useLocalParticipant,
	useRoomContext,
} from "@livekit/components-react";
import type {
	TrackReference,
	TrackReferenceOrPlaceholder,
} from "@livekit/components-core";
import { VideoTrack } from "../layout/VideoTrack";
import UserAvatarIcon from "../Participant/UserAvatarIcon";

interface PresenterCameraCardProps {
	publisherIdentity?: string;
}

const IGNORED_IDENTITIES = new Set(["Media", "shared-url", "remote-obs"]);
// Topic + cadence used to broadcast which participant is currently speaking.
// Every viewer (real participants and the egress template) listens to these
// packets and renders the same card, regardless of local speaker detection.
const SPEAKER_TOPIC = "presenter-speaker";
const HEARTBEAT_INTERVAL_MS = 1200;
const HEARTBEAT_TTL_MS = 4500;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

interface SpeakerHeartbeat {
	identity: string;
	receivedAt: number;
}

const isCandidate = (identity?: string) => {
	if (!identity) return false;
	if (IGNORED_IDENTITIES.has(identity)) return false;
	if (identity.startsWith("agent-")) return false;
	return true;
};

const PresenterCardBody: React.FC<{
	trackRef: TrackReferenceOrPlaceholder;
	participant: Participant;
	displayName: string;
}> = ({ trackRef, participant, displayName }) => {
	const isCameraMuted = useIsMuted(Track.Source.Camera, { participant });
	const hasPublishedTrack = !!trackRef.publication;
	const showVideo = hasPublishedTrack && !isCameraMuted;

	if (showVideo) {
		return (
			<VideoTrack
				trackRef={trackRef as TrackReference}
				className="presenterCardVideo"
			/>
		);
	}

	return (
		<div className="presenterCardPlaceholder">
			<UserAvatarIcon
				name={displayName}
				isSpeaking={participant.isSpeaking}
			/>
		</div>
	);
};

/**
 * Static (non-draggable) card overlaid on the presentation view that always
 * shows the camera of whoever is currently speaking.
 *
 * Consistency across viewers is achieved through a heartbeat data packet
 * (topic `presenter-speaker`): the local participant who is speaking
 * broadcasts their identity every {@link HEARTBEAT_INTERVAL_MS}, and every
 * viewer renders that identity. After {@link HEARTBEAT_TTL_MS} of silence
 * the card falls back to the presentation publisher.
 */
const PresenterCameraCard: React.FC<PresenterCameraCardProps> = ({
	publisherIdentity,
}) => {
	const room = useRoomContext();
	const { localParticipant } = useLocalParticipant();
	const isLocalSpeaking = useIsSpeaking(localParticipant);

	const cameraTracks = useTracks(
		[{ source: Track.Source.Camera, withPlaceholder: true }],
		{ updateOnlyOn: [RoomEvent.ActiveSpeakersChanged] }
	);

	const candidates = useMemo(
		() => cameraTracks.filter((t) => isCandidate(t.participant.identity)),
		[cameraTracks]
	);

	const publisherTrack = useMemo(() => {
		if (!publisherIdentity) return undefined;
		return candidates.find((t) => t.participant.identity === publisherIdentity);
	}, [candidates, publisherIdentity]);

	const [heartbeat, setHeartbeat] = useState<SpeakerHeartbeat | null>(null);

	useEffect(() => {
		if (!room) return;
		const handleDataReceived = (
			payload: Uint8Array,
			_participant?: RemoteParticipant,
			_kind?: DataPacket_Kind,
			topic?: string
		) => {
			if (topic !== SPEAKER_TOPIC) return;
			try {
				const data = JSON.parse(textDecoder.decode(payload));
				if (typeof data?.identity === "string" && data.identity) {
					setHeartbeat({ identity: data.identity, receivedAt: Date.now() });
				}
			} catch {
				/* ignore malformed packet */
			}
		};
		room.on(RoomEvent.DataReceived, handleDataReceived);
		return () => {
			room.off(RoomEvent.DataReceived, handleDataReceived);
		};
	}, [room]);

	useEffect(() => {
		if (!localParticipant || !isLocalSpeaking) return;
		const send = () => {
			const payload = textEncoder.encode(
				JSON.stringify({
					identity: localParticipant.identity,
					ts: Date.now(),
				})
			);
			localParticipant
				.publishData(payload, DataPacket_Kind.LOSSY, {
					topic: SPEAKER_TOPIC,
				})
				.catch(() => {
					/* lossy heartbeat — drop is acceptable */
				});
			setHeartbeat({
				identity: localParticipant.identity,
				receivedAt: Date.now(),
			});
		};
		send();
		const intervalId = window.setInterval(send, HEARTBEAT_INTERVAL_MS);
		return () => window.clearInterval(intervalId);
	}, [localParticipant, isLocalSpeaking]);

	useEffect(() => {
		if (!heartbeat) return;
		const remainingMs =
			HEARTBEAT_TTL_MS - (Date.now() - heartbeat.receivedAt);
		if (remainingMs <= 0) {
			setHeartbeat(null);
			return;
		}
		const timeoutId = window.setTimeout(() => {
			setHeartbeat((current) =>
				current && Date.now() - current.receivedAt >= HEARTBEAT_TTL_MS
					? null
					: current
			);
		}, remainingMs);
		return () => window.clearTimeout(timeoutId);
	}, [heartbeat]);

	const heartbeatTrack = useMemo(() => {
		if (!heartbeat) return undefined;
		return candidates.find(
			(t) => t.participant.identity === heartbeat.identity
		);
	}, [candidates, heartbeat]);

	const sortedCandidates = useMemo(() => {
		return [...candidates].sort((a, b) =>
			(a.participant.identity || "").localeCompare(
				b.participant.identity || ""
			)
		);
	}, [candidates]);

	const displayed: TrackReferenceOrPlaceholder | undefined = useMemo(() => {
		if (heartbeatTrack) return heartbeatTrack;
		if (publisherTrack) return publisherTrack;
		return sortedCandidates[0];
	}, [heartbeatTrack, publisherTrack, sortedCandidates]);

	if (!displayed) return null;

	const participantName =
		displayed.participant.name || displayed.participant.identity || "";
	const isShownSpeaking =
		!!heartbeat && heartbeat.identity === displayed.participant.identity;

	return (
		<div className="presenterCard" title={participantName}>
			<div className="presenterCardHandle">
				<span className="presenterCardLabel">{participantName}</span>
				{isShownSpeaking && <span className="presenterCardSpeakingDot" />}
			</div>
			<div className="presenterCardBody">
				<PresenterCardBody
					key={displayed.participant.sid}
					trackRef={displayed}
					participant={displayed.participant}
					displayName={participantName}
				/>
			</div>
		</div>
	);
};

export default PresenterCameraCard;
